import { useEffect } from 'react';
import { useTourContext } from '@/store/tourContext';
import type { TourElementKey } from '@/utils/tutorials';
import { tutorialActions } from '@/utils/tutorials';
import { useTutorialActive } from './useTutorialActive';

interface UseReturnsTutorialProps {
	returnsTableRef: React.RefObject<HTMLDivElement | null>;
	returnsActionColumnRef: React.RefObject<HTMLElement | null>;
}

/**
 * Custom hook to manage all Returns tutorial logic
 * Handles tutorial actions, ref registrations, and form step navigation
 */
export const useReturnsTutorial = ({
	returnsTableRef,
	returnsActionColumnRef,
}: UseReturnsTutorialProps): void => {
	const { registerElement, activeTutorialName } = useTourContext();
	const { isReturnsTutorialActive: isReturnsTutorialWCActive, isOnboardingActive } =
		useTutorialActive();
	
	const isAnyReturnsTutorialActive = 
		isReturnsTutorialWCActive ||
		(isOnboardingActive && 
			(activeTutorialName === 'OnboardingMindsightTutorial' ||
				activeTutorialName === 'OnboardingWCTutorial' ||
				activeTutorialName === 'OnboardingSalesRepTutorial'));

	// Register expandActionColumn action
	useEffect(() => {
		// Action to expand action column (for viewing return details)
		tutorialActions.register('expandActionColumn', () => {
			if (isAnyReturnsTutorialActive && returnsTableRef.current) {
				// Find the first row's action button and click it
				setTimeout(() => {
					const tbody = returnsTableRef.current?.querySelector('tbody');
					if (tbody) {
						const firstRow = tbody.querySelector('tr:first-child');
						if (firstRow) {
							const cells = firstRow.querySelectorAll('td');
							if (cells.length > 0) {
								const actionCell = cells[cells.length - 1];
								const trigger = actionCell.querySelector(
									'button, [role="button"]',
								) as HTMLElement;
								if (trigger) {
									trigger.click();
								}
							}
						}
					}
				}, 300);
			}
		});

		return () => {
			tutorialActions.unregister('expandActionColumn');
		};
	}, [isAnyReturnsTutorialActive, returnsTableRef]);

	// Register refs for Returns tutorial elements
	useEffect(() => {
		if (!isAnyReturnsTutorialActive) {
			// Clear all refs when tutorial is not active
			const refsToClear: TourElementKey[] = [
				'ReturnsNavItemRef',
				'ReturnsAddButtonRef',
				'ReturnsPatientFormRef',
				'ReturnsQuestionnaireRef',
				'ReturnsTissueIdFieldRef',
				'ReturnsExpirationDateFieldRef',
				'ReturnsDateReturnedFieldRef',
				'ReturnsNewShippingDateFieldRef',
				'ReturnsSubmitButtonRef',
				'ReturnsActionColumnRef',
				'ReturnsFilterDropdownRef',
			];
			refsToClear.forEach((ref) => {
				registerElement(ref, null);
			});
			return;
		}

		// Register Add Button ref - use the ref passed from DataTable
		const timeoutId = setTimeout(() => {
			if (returnsTableRef.current) {
				// Look for the add button using the title attribute
				const addButton = returnsTableRef.current.querySelector(
					'button[title="Add"], button[aria-label*="Add"]',
				) as HTMLElement;
				if (addButton) {
					registerElement('ReturnsAddButtonRef', addButton);
				}

				let statusFilterInput: HTMLElement | null = null;
				
				const allHeaders = returnsTableRef.current.querySelectorAll('thead tr:last-child th');
				if (allHeaders) {
					Array.from(allHeaders).forEach((header, index) => {
						const headerText = header.textContent?.trim() || '';
						if (headerText === 'Status' || headerText.includes('Status')) {
							const thead = header.closest('thead');
							if (thead) {
								const filterRow = thead.querySelector('tr:first-child');
								if (filterRow) {
									const filterCells = filterRow.querySelectorAll('th');
									if (filterCells[index]) {
										const input = filterCells[index].querySelector('input[placeholder="Filter"]') as HTMLElement;
										if (input) {
											statusFilterInput = input;
										}
									}
								}
							}
						}
					});
				}
				
				if (!statusFilterInput) {
					const thead = returnsTableRef.current.querySelector('thead');
					if (thead) {
						const filterRow = thead.querySelector('tr:first-child');
						if (filterRow) {
							const filterInputs = filterRow.querySelectorAll('input[placeholder="Filter"]');
							if (filterInputs.length >= 6) {
								statusFilterInput = filterInputs[5] as HTMLElement || filterInputs[6] as HTMLElement;
							}
						}
					}
				}
				
				if (statusFilterInput) {
					registerElement('ReturnsFilterDropdownRef', statusFilterInput);
				}
			}
		}, 1000);

		return () => {
			clearTimeout(timeoutId);
		};
	}, [isAnyReturnsTutorialActive, registerElement, returnsTableRef]);

	useEffect(() => {
		if (!isAnyReturnsTutorialActive) {
			registerElement('ReturnsActionColumnRef', null);
			if (returnsActionColumnRef) {
				returnsActionColumnRef.current = null;
			}
			return;
		}

		// Helper function to find and register the action button
		const findAndRegisterActionButton = () => {
			const tableRoot = returnsTableRef.current;
			if (!tableRoot) return false;

			// Find the tbody
			const tbody = tableRoot.querySelector('tbody');
			if (!tbody) return false;

			// Find the first row - try multiple selectors
			let firstRow = tbody.querySelector('tr:first-child');
			if (!firstRow) {
				// Try finding any row
				firstRow = tbody.querySelector('tr');
			}
			if (!firstRow) return false;

			// Find the last cell (where the action menu is)
			const cells = firstRow.querySelectorAll('td');
			if (cells.length === 0) return false;

			const actionCell = cells[cells.length - 1];
			if (!actionCell) return false;

			// Find the Box that contains the ActionMenuIcon (the actual clickable icon container)
			// First, find the SVG icon, then find its parent Box with display: flex
			let trigger: HTMLElement | null = null;
			const svgIcon = actionCell.querySelector('svg');
			
			if (svgIcon) {
				// Find the parent div that has display: flex and cursor: pointer
				let parent = svgIcon.parentElement;
				while (parent && parent !== actionCell && parent !== tableRoot) {
					const style = window.getComputedStyle(parent);
					if (style.display === 'flex' && style.cursor === 'pointer') {
						trigger = parent as HTMLElement;
						break;
					}
					parent = parent.parentElement;
				}
			}
			
			// Fallback: look for [role="button"] which is the CustomMenu.Trigger
			if (!trigger) {
				trigger = actionCell.querySelector('[role="button"]') as HTMLElement;
			}
			
			// Another fallback: look for button element
			if (!trigger) {
				trigger = actionCell.querySelector('button') as HTMLElement;
			}
			
			// Another fallback: look for any clickable element with cursor pointer
			if (!trigger) {
				const allClickable = actionCell.querySelectorAll('*');
				for (const el of Array.from(allClickable)) {
					const style = window.getComputedStyle(el);
					if (style.cursor === 'pointer') {
						trigger = el as HTMLElement;
						break;
					}
				}
			}
			
			// Last fallback: the SVG icon itself
			if (!trigger && svgIcon) {
				trigger = svgIcon as unknown as HTMLElement;
			}

			if (trigger) {
				// Update the ref so it can be used in useAppTour
				if (returnsActionColumnRef) {
					returnsActionColumnRef.current = trigger;
				}
				registerElement('ReturnsActionColumnRef', trigger);
				return true;
			}
			return false;
		};

		// Try immediately
		let found = findAndRegisterActionButton();
		
		// Timeout IDs for cleanup
		const timeoutIds: NodeJS.Timeout[] = [];
		
		// If not found, retry with delays
		if (!found) {
			const timeout1 = setTimeout(() => {
				found = findAndRegisterActionButton();
				if (!found) {
					const timeout2 = setTimeout(() => {
						findAndRegisterActionButton();
					}, 500);
					timeoutIds.push(timeout2);
				}
			}, 200);
			timeoutIds.push(timeout1);
		}

		// Also set up an interval to keep checking (in case table loads later)
		const intervalId = setInterval(() => {
			if (!returnsActionColumnRef?.current) {
				findAndRegisterActionButton();
			}
		}, 1000);

		return () => {
			timeoutIds.forEach(id => clearTimeout(id));
			clearInterval(intervalId);
			// Clear the ref when tutorial is not active
			if (returnsActionColumnRef) {
				returnsActionColumnRef.current = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAnyReturnsTutorialActive, registerElement, returnsTableRef]);

	// All tutorial logic is handled internally via effects and action registrations
};

